/**
 * A part's chord chart, stored as chartData (see chartData.js).
 */

import { countGridBars, lyricsMatchChart } from './chordBars';
import { chartDataToGrid, hasChartData } from './chartData';
import { findChords } from './chordPatterns';
import { canChart, isInstrumentalPart } from './parts';

/** The part's chart as chartData, or null. */
export const getPartChart = (part) => (hasChartData(part?.chartData) ? part.chartData : null);

/** The part's chart as the grid the chart and the player draw, `{ lines, sections }`, or null. */
export const getPartGrid = (part, beatsPerBar, options) => chartDataToGrid(getPartChart(part), beatsPerBar, options);

export const hasPartChart = (part) => Boolean(getPartChart(part));

/**
 * Whether the chart is drawn on the page. An instrumental's chart is its body and is drawn in its
 * lyrics row; a part made of words keeps its chart beside them, opened from the part chip.
 */
export const isChartDrawn = (part) => isInstrumentalPart(part?.name);

/**
 * The parts carrying a chart, in reading order — the words' own charts and the instrumentals' alike.
 *
 * Duplicates are left out although they carry a copy of it: a repeat plays the chart its original
 * states, so listing both would offer the same chart twice.
 */
export const getChartedParts = (parts = []) => parts.filter((part) => !part.isDuplicate && hasPartChart(part));

/**
 * How much of the song is charted: the parts that can carry a chart, and those of them that don't.
 * Notes and section headings can't, and a repeat plays its original's chart, so none of them count.
 *
 * @returns {{ total: number, missing: object[] }}
 */
export const getChartCoverage = (parts = []) => {
  const chartable = parts.filter((part) => !part.isDuplicate && canChart(part.name));

  return { total: chartable.length, missing: chartable.filter((part) => !hasPartChart(part)) };
};

/**
 * The same count for a song in the team list, which carries no parts but `chartCoverage` worked out
 * by the server. A song swapped in from a save has its parts, and they are the fresher of the two.
 *
 * @returns {{ total: number, charted: number } | null}
 */
export const getSongChartCoverage = (song) => {
  if (Array.isArray(song?.parts)) {
    const { total, missing } = getChartCoverage(song.parts);

    return { total, charted: total - missing.length };
  }

  return song?.chartCoverage ?? null;
};

/**
 * The bars a word part's chart states it runs for, or null when its length can't be read off it.
 *
 * A chart kept beside the words can be one pass of the part — imported off the chords over the page
 * — or a loop the words go round several times, and only the first states how long the part is. A
 * chart playing the page's chords, in the page's order, is that page. A part with no chords written
 * over it has nothing to contradict its chart, so the chart stands.
 *
 * Instrumentals are timed off their chart as bar lines instead (see songTiming), so they're null here.
 */
export const chartBars = (part, lyrics, beatsPerBar) => {
  if (!part || isChartDrawn(part)) return null;

  const chart = getPartGrid(part, beatsPerBar);
  if (!chart) return null;

  const bars = countGridBars(chart.lines, chart.sections);
  if (bars <= 0) return null;

  const written = findChords(lyrics ?? '', { display: true });

  return written.length === 0 || lyricsMatchChart(chart.lines, lyrics ?? '') ? bars : null;
};
